import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  Send,
  X,
  Bot,
  User,
  Loader2,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Calculator,
  LineChart,
  CreditCard,
  BookOpen,
  PiggyBank,
  Target
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick suggestion buttons based on category
const getQuickSuggestions = (category) => {
  const suggestions = {
    debtFree: [
      "How do I pay off debt faster?",
      "Snowball vs Avalanche method?",
      "Should I save or pay debt first?"
    ],
    startInvesting: [
      "How do I start investing?",
      "What is a TFSA?",
      "What are ETFs?"
    ],
    buildWealth: [
      "How to diversify my portfolio?",
      "TFSA vs RRSP which is better?",
      "How does compound interest work?"
    ],
    learnFinance: [
      "Explain investing basics",
      "What should I learn first?",
      "How do stocks work?"
    ],
    planSave: [
      "How to create a budget?",
      "50/30/20 budget rule?",
      "How much should I save?"
    ]
  };
  return suggestions[category] || suggestions.startInvesting;
};

// Tool suggestions to display
const TOOL_SUGGESTIONS = [
  { name: "ETF Builder", icon: PiggyBank, path: "/tools?tool=etf-builder", color: "bg-emerald-500" },
  { name: "Stock Simulator", icon: LineChart, path: "/tools?tool=simulator", color: "bg-blue-500" },
  { name: "Debt Calculator", icon: CreditCard, path: "/tools?tool=debt", color: "bg-red-500" },
  { name: "Learn Hub", icon: BookOpen, path: "/tools?tool=learnhub", color: "bg-purple-500" },
];

const AIFinancialCoach = ({ investorType, recommendedCategory, userName, isOpen: controlledIsOpen, onOpenChange }) => {
  // Support both controlled and uncontrolled modes
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = (value) => {
    if (onOpenChange) {
      onOpenChange(value);
    }
    setInternalIsOpen(value);
  };
  
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  // Add welcome message when chat first opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const categoryNames = {
        debtFree: "getting debt-free",
        startInvesting: "starting your investment journey",
        buildWealth: "building wealth",
        learnFinance: "learning finance",
        planSave: "planning and saving"
      };
      
      setMessages([{
        role: "assistant",
        content: `Hi${userName ? ` ${userName}` : ""}! 👋 I'm your AI Financial Coach. Based on your profile, I see you're focused on ${categoryNames[recommendedCategory] || "improving your finances"}. How can I help you today?\n\nYou can ask me about budgeting, investing basics, debt strategies, or our tools. I'm here to guide you!`
      }]);
    }
  }, [isOpen, userName, recommendedCategory]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const userData = localStorage.getItem("user_data");
      let userId = "anonymous";
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          userId = parsed.email || parsed.id || "anonymous";
        } catch (e) {}
      }

      // Use fetch instead of axios for better error handling
      const response = await fetch(`${API_URL}/api/ai-coach/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          user_id: userId,
          investor_type: investorType || "",
          recommended_category: recommendedCategory || "",
          conversation_history: messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content
          }))
        })
      });

      const data = await response.json();
      
      if (data && data.response) {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: data.response 
        }]);
      } else if (data && data.error) {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: data.error 
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "I received an unexpected response. Please try again." 
        }]);
      }
    } catch (error) {
      console.error("AI Coach error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: `Connection error: ${error.message || "Please try again."}` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickSuggestion = async (suggestion) => {
    if (isLoading) return;
    
    // Add user message directly
    setMessages(prev => [...prev, { role: "user", content: suggestion }]);
    setIsLoading(true);

    try {
      const userData = localStorage.getItem("user_data");
      let userId = "anonymous";
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          userId = parsed.email || parsed.id || "anonymous";
        } catch (e) {}
      }

      const response = await fetch(`${API_URL}/api/ai-coach/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: suggestion,
          user_id: userId,
          investor_type: investorType || "",
          recommended_category: recommendedCategory || "",
          conversation_history: messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content
          }))
        })
      });

      const data = await response.json();

      if (data && data.response) {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: data.response 
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "I received an unexpected response. Please try again." 
        }]);
      }
    } catch (error) {
      console.error("AI Coach error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: `Connection error: ${error.message || "Please try again."}` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickSuggestions = getQuickSuggestions(recommendedCategory);

  // Minimized chat button
  if (!isOpen) {
    return (
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="fixed bottom-6 right-6 z-50"
      >
        <Button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-lg"
          data-testid="ai-coach-open-btn"
        >
          <MessageCircle className="w-6 h-6" />
        </Button>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)]"
      data-testid="ai-coach-panel"
    >
      <Card className="border-0 shadow-2xl overflow-hidden">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-base font-heading">AI Financial Coach</CardTitle>
                <p className="text-xs text-emerald-100">Powered by Perplexity</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(!isMinimized)}
                className="text-white hover:bg-white/20 w-8 h-8 p-0"
              >
                {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white/20 w-8 h-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Chat Content */}
        <AnimatePresence>
          {!isMinimized && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
            >
              <CardContent className="p-0">
                {/* Messages Area */}
                <ScrollArea className="h-[350px] p-4">
                  <div className="space-y-4">
                    {messages.map((msg, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          msg.role === "user" ? "bg-blue-500" : "bg-emerald-500"
                        }`}>
                          {msg.role === "user" ? (
                            <User className="w-4 h-4 text-white" />
                          ) : (
                            <Bot className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className={`max-w-[80%] rounded-2xl p-3 ${
                          msg.role === "user" 
                            ? "bg-blue-500 text-white rounded-tr-sm" 
                            : "bg-slate-100 text-slate-700 rounded-tl-sm"
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </motion.div>
                    ))}
                    
                    {/* Loading indicator */}
                    {isLoading && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-slate-100 rounded-2xl rounded-tl-sm p-3">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                            <span className="text-sm text-slate-500">Thinking...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Quick Suggestions */}
                {messages.length <= 1 && (
                  <div className="px-4 pb-2">
                    <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
                    <div className="flex flex-wrap gap-2">
                      {quickSuggestions.map((suggestion, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className="cursor-pointer hover:bg-emerald-50 hover:border-emerald-300 text-xs"
                          onClick={() => handleQuickSuggestion(suggestion)}
                        >
                          {suggestion}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input Area */}
                <div className="p-4 border-t bg-slate-50">
                  <div className="flex gap-2">
                    <Input
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                      placeholder="Ask me anything about finance..."
                      className="flex-1 bg-white"
                      disabled={isLoading}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || isLoading}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
};

export default AIFinancialCoach;
